/* platform/PlatformUsers — Control Center IAM. Manages SA/PA/Support/Billing/Auditor users.
   NOT organization users. Route: /platform/users */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, Th, TenancyModal } from './_ui';
import { getPlatformRole, can, PLATFORM_ROLES, getRoleInfo } from './rbac.js';
import * as puApi from '../api/platform-users.api.js';
import * as authApi from '../api/auth.api.js';
import * as inviteApi from '../api/platform-invite.api.js';
import { exportCSV, exportExcel, COLS } from './utils/exportUtils.js';

const ROLE_OPTIONS = Object.entries(PLATFORM_ROLES).map(([value, info]) => ({ value, label: info.label }));

const EMPTY_INVITE = { name: '', email: '', role: 'PLATFORM_ADMIN' };

/* Derive access state from role fields */
const getAccessState = (u) => {
  if (!u.platformRole && !u.platformCustomRole) return 'NO_ACCESS';
  return u.status || 'ACTIVE';
};

const normUser = (u) => ({
  id:               u.id,
  name:             u.name        || '—',
  email:            u.email       || '—',
  role:             u.platformRole || (u.platformCustomRole ? 'CUSTOM' : null),
  customRole:       u.platformCustomRole?.name || null,
  status:           u.status     || 'ACTIVE',
  hasPendingInvite: u.hasPendingInvite ?? false,
  lastLogin:        u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—',
  created:          u.createdAt   ? new Date(u.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—',
});

/* Canonical status display label and tone */
const getDisplayStatus = (u) => {
  if (!u.role) return { label: 'No Access', tone: 'slate' };
  if (u.status === 'SUSPENDED') return { label: 'Suspended', tone: 'amber' };
  return { label: 'Active', tone: 'green' };
};

export default function PlatformUsers() {
  const toast       = useToast();
  const navigate    = useNavigate();
  const [users,    setUsers]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [q,        setQ]        = useState('');
  const [invite,      setInvite]      = useState(null);
  const [inviteBusy,  setInviteBusy]  = useState(false);
  const [inviteLink,  setInviteLink]  = useState(null);
  const [editRole,    setEditRole]    = useState(null);
  const [suspendConfirm,    setSuspendConfirm]    = useState(null);
  const [removeAccessConfirm, setRemoveAccessConfirm] = useState(null);

  const myRole    = getPlatformRole();
  const canWrite  = can(myRole, 'write');
  const canInvite = myRole === 'SUPER_ADMIN';
  const canDelete = myRole === 'SUPER_ADMIN';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await puApi.list();
      const data = Array.isArray(res) ? res : (res?.data ?? []);
      setUsers(data.map(normUser));
    } catch (_) {
      setUsers([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => !q ||
    u.name.toLowerCase().includes(q.toLowerCase()) ||
    u.email.toLowerCase().includes(q.toLowerCase()) ||
    (u.role || '').toLowerCase().includes(q.toLowerCase()));

  /* ── Invite ──────────────────────────────────────────────── */
  const handleInvite = async () => {
    if (!invite.name.trim() || !invite.email.trim()) return;
    setInviteBusy(true);
    try {
      const result = await inviteApi.invite(invite.name.trim(), invite.email.trim(), invite.role);
      const link = window.location.origin + '/platform-invite/' + result.token;
      setInvite(null);
      setInviteLink({ link, name: invite.name.trim(), email: invite.email.trim(), expiresAt: result.expiresAt });
      load();
    } catch (err) {
      toast({ tone: 'error', icon: 'AlertCircle', title: 'Invite failed', desc: err?.message || 'Something went wrong' });
    } finally { setInviteBusy(false); }
  };

  /* ── Suspend Access ──────────────────────────────────────── */
  const handleSuspend = async (u) => {
    setSuspendConfirm(null);
    try {
      await puApi.deactivate(u.id);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: 'SUSPENDED' } : x));
      toast({ tone: 'warning', icon: 'AlertCircle', title: u.name + ' suspended', desc: 'Role and account preserved.' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err.message }); }
  };

  /* ── Reactivate ──────────────────────────────────────────── */
  const handleReactivate = async (u) => {
    try {
      await puApi.activate(u.id);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, status: 'ACTIVE' } : x));
      toast({ tone: 'success', icon: 'Check', title: u.name + ' reactivated' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Action failed', desc: err.message }); }
  };

  /* ── Reset password ──────────────────────────────────────── */
  const handleReset = async (u) => {
    try {
      await authApi.forgotPassword(u.email);
      toast({ tone: 'success', icon: 'Key', title: 'Reset link sent', desc: u.email });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
  };

  /* ── Update role ─────────────────────────────────────────── */
  const handleRoleUpdate = async (u, newRole) => {
    try {
      await puApi.updateRole(u.id, newRole);
      setUsers(prev => prev.map(x => x.id === u.id ? { ...x, role: newRole } : x));
      toast({ tone: 'success', icon: 'Shield', title: 'Role updated', desc: u.name + ' → ' + PLATFORM_ROLES[newRole]?.label });
      setEditRole(null);
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
  };

  /* ── Remove Access ───────────────────────────────────────── */
  const handleRemoveAccess = async (u) => {
    setRemoveAccessConfirm(null);
    try {
      await puApi.revoke(u.id);
      // Keep user in list with "No Access" status rather than removing them
      setUsers(prev => prev.map(x => x.id === u.id
        ? { ...x, role: null, customRole: null, status: 'ACTIVE' }
        : x));
      toast({ tone: 'warning', icon: 'ShieldOff', title: 'Access removed', desc: u.name + ' — account retained, no platform role.' });
    } catch (err) { toast({ tone: 'error', icon: 'AlertCircle', title: 'Failed', desc: err.message }); }
  };

  /* ── Stats ───────────────────────────────────────────────── */
  const active    = users.filter(u => u.status === 'ACTIVE' && u.role).length;
  const suspended = users.filter(u => u.status === 'SUSPENDED').length;
  const noAccess  = users.filter(u => !u.role && !u.hasPendingInvite).length;
  const saCount   = users.filter(u => u.role === 'SUPER_ADMIN').length;

  return (
    <div className="nx-cc-page">
      <PageHead
        title="Platform Users"
        sub="Control Center administrators only — not organization users"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" icon="Download" onClick={() => exportCSV('platform-users', filtered, COLS.platformUsers)}>CSV</Btn>
            <Btn variant="ghost" icon="FileSpreadsheet" onClick={() => exportExcel('platform-users', [{ name: 'Platform Users', rows: filtered, columns: COLS.platformUsers }])}>Excel</Btn>
            {canInvite && (
              <Btn variant="primary" icon="UserPlus" onClick={() => setInvite({ ...EMPTY_INVITE })}>
                Invite User
              </Btn>
            )}
          </div>
        }
      />

      <div className="nx-cc-stat-grid" style={{ gridTemplateColumns: 'repeat(5,1fr)' }}>
        <StatCard label="Total"        value={users.length} icon="Users"       color="#125AB0" />
        <StatCard label="Active"       value={active}       icon="Check"       color="#0E8C5A" />
        <StatCard label="Suspended"    value={suspended}    icon="AlertCircle" color="#C26A1F" />
        <StatCard label="No Access"    value={noAccess}     icon="ShieldOff"   color="#6B7280" />
        <StatCard label="Super Admins" value={saCount}      icon="Shield"      color="#6E3FBA" />
      </div>

      <div style={{ margin: '14px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
        <div className="nx-search-inline" style={{ width: 300 }}>
          <NexusIcon name="Search" size={13} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, email, or role…" />
        </div>
        <Btn variant="ghost" icon="RefreshCw" onClick={load}>Refresh</Btn>
      </div>

      {loading ? (
        <div className="nx-empty"><NexusIcon name="Loader" size={20} /><span>Loading platform users…</span></div>
      ) : filtered.length === 0 ? (
        <div className="nx-empty">
          <NexusIcon name="Users" size={24} />
          <span>{q ? 'No users match your search.' : 'No platform users found.'}</span>
          {!q && canInvite && <Btn variant="secondary" icon="UserPlus" onClick={() => setInvite({ ...EMPTY_INVITE })}>Invite the first user</Btn>}
        </div>
      ) : (
        <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
          <thead><tr>
            <Th icon="User">Name</Th>
            <Th icon="Mail">Email</Th>
            <Th icon="Shield">Role</Th>
            <Th icon="Activity">Status</Th>
            <Th icon="Clock">Last Login</Th>
            <Th icon="Calendar">Created</Th>
            <Th end />
          </tr></thead>
          <tbody>{filtered.map(u => {
            const ri = u.role === 'CUSTOM'
              ? { label: u.customRole || 'Custom', tone: 'blue' }
              : u.role
                ? getRoleInfo(u.role)
                : { label: 'No Access', tone: 'slate' };
            const ds = getDisplayStatus(u);
            const isSelf = u.email === (getSessionUser()?.email);
            const isSuspended = u.status === 'SUSPENDED';
            const hasAccess = !!u.role;

            return (
              <tr key={u.id} className="nx-tr" style={{ cursor: 'pointer' }} onClick={() => navigate('/platform/users/' + u.id)}>
                <td>
                  <span className="nx-row-name">
                    <span className="nx-domain-mark" style={{ width: 28, height: 28, fontSize: 11, background: u.role === 'SUPER_ADMIN' ? '#6E3FBA' : u.role === 'PLATFORM_ADMIN' ? '#125AB0' : hasAccess ? '#0E8C5A' : '#9CA3AF' }}>
                      {u.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                    </span>
                    <div>
                      <div className="nx-row-name-text">{u.name}</div>
                      {isSelf && <span style={{ fontSize: 10, color: 'var(--nx-text-muted)' }}>You</span>}
                    </div>
                  </span>
                </td>
                <td><span className="nx-muted">{u.email}</span></td>
                <td onClick={e => e.stopPropagation()}>
                  {canWrite && !isSelf && myRole === 'SUPER_ADMIN' && u.role && u.role !== 'SUPER_ADMIN' ? (
                    editRole === u.id ? (
                      <select className="nx-input" style={{ width: 150, fontSize: 12 }}
                        defaultValue={u.role}
                        onChange={e => handleRoleUpdate(u, e.target.value)}
                        onBlur={() => setEditRole(null)}>
                        {ROLE_OPTIONS.filter(r => r.value !== 'SUPER_ADMIN').map(r => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span style={{ cursor: 'pointer' }} onClick={() => setEditRole(u.id)} title="Click to change role">
                        <Pill tone={ri.tone}>{ri.label}</Pill>
                        <NexusIcon name="Pencil" size={11} style={{ marginLeft: 4, color: 'var(--nx-text-faint, #CBD5E1)', verticalAlign: 'middle' }} />
                      </span>
                    )
                  ) : (
                    <Pill tone={ri.tone}>{ri.label}</Pill>
                  )}
                </td>
                <td>
                  {u.hasPendingInvite
                    ? <Pill tone="amber">Invited</Pill>
                    : <Pill tone={ds.tone} data-testid={`pu-status-${u.id}`}>{ds.label}</Pill>}
                </td>
                <td><span className="nx-muted nx-mini">{u.lastLogin}</span></td>
                <td><span className="nx-muted nx-mini">{u.created}</span></td>
                <td className="nx-td-end" style={{ whiteSpace: 'nowrap' }} onClick={e => e.stopPropagation()}>
                  {canWrite && !isSelf && (
                    <>
                      {/* ACTIVE with role → Suspend Access */}
                      {hasAccess && !isSuspended && (
                        <Btn variant="ghost" size="sm" icon="AlertCircle"
                          data-testid={`pu-suspend-${u.id}`}
                          onClick={() => setSuspendConfirm(u)}>
                          Suspend Access
                        </Btn>
                      )}
                      {/* SUSPENDED → Reactivate */}
                      {isSuspended && (
                        <Btn variant="ghost" size="sm" icon="Check"
                          data-testid={`pu-reactivate-${u.id}`}
                          onClick={() => handleReactivate(u)}>
                          Reactivate
                        </Btn>
                      )}
                      {/* No role → Assign Role (navigate to detail) */}
                      {!hasAccess && (
                        <Btn variant="ghost" size="sm" icon="Shield"
                          data-testid={`pu-assign-${u.id}`}
                          onClick={() => navigate('/platform/users/' + u.id)}>
                          Assign Role
                        </Btn>
                      )}
                      <Btn variant="ghost" size="sm" icon="Key" onClick={() => handleReset(u)}>Reset</Btn>
                    </>
                  )}
                  {canDelete && !isSelf && hasAccess && (
                    <Btn variant="ghost" size="sm" icon="ShieldOff"
                      data-testid={`pu-remove-${u.id}`}
                      onClick={() => setRemoveAccessConfirm(u)}>
                      Remove Access
                    </Btn>
                  )}
                </td>
              </tr>
            );
          })}</tbody>
        </table></div></div>
      )}

      {/* ── Invite modal ──────────────────────────────────────── */}
      {invite && (
        <TenancyModal title="Invite Platform User" icon="UserPlus" onClose={() => setInvite(null)}>
          <div className="nx-form" style={{ gap: 10 }}>
            <div className="nx-form-row">
              <label>Full Name <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input className="nx-input" placeholder="Jane Smith" value={invite.name}
                onChange={e => setInvite(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="nx-form-row">
              <label>Email <span style={{ color: 'var(--nx-danger)' }}>*</span></label>
              <input className="nx-input" type="email" placeholder="jane@nuacem.com" value={invite.email}
                onChange={e => setInvite(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div className="nx-form-row">
              <label>Role</label>
              <select className="nx-input" value={invite.role} onChange={e => setInvite(f => ({ ...f, role: e.target.value }))}>
                {ROLE_OPTIONS.filter(r => myRole === 'SUPER_ADMIN' || r.value !== 'SUPER_ADMIN').map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
            <p className="nx-muted nx-mini" style={{ marginTop: 4 }}>
              An invite link will be generated. Share it with the user — they set their own password when accepting.
            </p>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 16 }}>
            <Btn variant="ghost" onClick={() => setInvite(null)}>Cancel</Btn>
            <Btn variant="primary" icon="UserPlus" disabled={inviteBusy || !invite.name.trim() || !invite.email.trim()} onClick={handleInvite}>
              {inviteBusy ? 'Sending…' : 'Send Invite'}
            </Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Invite link modal ────────────────────────────────── */}
      {inviteLink && (
        <TenancyModal title="Invite Link Created" icon="Link" onClose={() => setInviteLink(null)}>
          <div style={{ marginBottom: 12 }}>
            <p className="nx-muted nx-mini" style={{ marginBottom: 8 }}>
              Share this link with <strong>{inviteLink.name}</strong> ({inviteLink.email}). It expires in 48 hours.
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                className="nx-input"
                readOnly
                value={inviteLink.link}
                style={{ flex: 1, fontSize: 12, fontFamily: 'monospace' }}
                onFocus={e => e.target.select()}
              />
              <Btn variant="ghost" size="sm" icon="Copy" onClick={() => {
                navigator.clipboard.writeText(inviteLink.link);
                toast({ tone: 'success', icon: 'Copy', title: 'Copied to clipboard' });
              }}>Copy</Btn>
            </div>
          </div>
          <div className="nx-confirm-actions" style={{ marginTop: 12 }}>
            <Btn variant="primary" onClick={() => setInviteLink(null)}>Done</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Suspend Access confirm ────────────────────────────── */}
      {suspendConfirm && (
        <TenancyModal title={'Suspend Access — ' + suspendConfirm.name} icon="AlertCircle" onClose={() => setSuspendConfirm(null)}>
          <p className="nx-confirm-desc">
            This will temporarily prevent <strong>{suspendConfirm.name}</strong> from accessing URSA.
            Their role and account will be preserved. You can reactivate them at any time.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setSuspendConfirm(null)}>Cancel</Btn>
            <Btn variant="warning" icon="AlertCircle" data-testid="pu-suspend-confirm-btn" onClick={() => handleSuspend(suspendConfirm)}>Suspend Access</Btn>
          </div>
        </TenancyModal>
      )}

      {/* ── Remove Access confirm ─────────────────────────────── */}
      {removeAccessConfirm && (
        <TenancyModal title={'Remove Access — ' + removeAccessConfirm.name} icon="ShieldOff" danger onClose={() => setRemoveAccessConfirm(null)}>
          <p className="nx-confirm-desc">
            This will remove <strong>{removeAccessConfirm.name}</strong>'s platform role/access.
            Their user account will remain in URSA and can be assigned another role later.
            This is not account deletion.
          </p>
          <div className="nx-confirm-actions">
            <Btn variant="ghost" onClick={() => setRemoveAccessConfirm(null)}>Cancel</Btn>
            <Btn variant="danger" icon="ShieldOff" data-testid="pu-remove-confirm-btn" onClick={() => handleRemoveAccess(removeAccessConfirm)}>Remove Access</Btn>
          </div>
        </TenancyModal>
      )}
    </div>
  );
}

const getSessionUser = () => {
  try {
    if (window.__nx_auth?.user) return window.__nx_auth.user;
    return JSON.parse(localStorage.getItem('nx-session-v1'));
  } catch (_) { return null; }
};
